import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api } from "../lib/apiClient";
import { StatusBadge, PriorityBadge } from "../components/Badges";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import { Search, PlusCircle, Flag } from "lucide-react";

const ALL = "__all__";

export default function Complaints() {
  const [list, setList] = useState([]);
  const [meta, setMeta] = useState({ statuses: [], priorities: [] });
  const [engineers, setEngineers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();

  const [filters, setFilters] = useState({
    complaint_no: "", mobile: "", bill_number: "",
    status: "", priority: "", engineer_id: "",
    date_from: "", date_to: "", flag: params.get("flag") || "",
  });

  useEffect(() => {
    api.get("/meta").then((r) => setMeta(r.data));
    api.get("/engineers").then((r) => setEngineers(r.data));
  }, []);

  const load = () => {
    setLoading(true);
    const q = Object.fromEntries(Object.entries(filters).filter(([, v]) => v));
    api.get("/complaints", { params: q }).then((r) => setList(r.data)).finally(() => setLoading(false));
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const update = (k, v) => setFilters((f) => ({ ...f, [k]: v }));

  return (
    <div className="p-5 sm:p-8 fade-up">
      <div className="flex items-end justify-between mb-6 flex-wrap gap-3">
        <div>
          <div className="label-caps text-muted-foreground">Records</div>
          <h1 className="font-heading text-3xl sm:text-4xl font-black tracking-tighter">All Complaints</h1>
        </div>
        <Button onClick={() => navigate("/complaints/new")} data-testid="new-complaint-button"
          className="rounded-sm bg-klein hover:bg-klein/90 text-white">
          <PlusCircle size={16} className="mr-1" /> New Complaint
        </Button>
      </div>

      {/* Filters */}
      <div className="border border-border bg-white p-4 mb-5 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
        <Input placeholder="Complaint no." value={filters.complaint_no} data-testid="filter-complaint-no"
          onChange={(e) => update("complaint_no", e.target.value)} className="rounded-sm" />
        <Input placeholder="Mobile" value={filters.mobile} data-testid="filter-mobile"
          onChange={(e) => update("mobile", e.target.value)} className="rounded-sm" />
        <Input placeholder="Bill / invoice no." value={filters.bill_number} data-testid="filter-bill"
          onChange={(e) => update("bill_number", e.target.value)} className="rounded-sm" />
        <Select value={filters.status || ALL} onValueChange={(v) => update("status", v === ALL ? "" : v)}>
          <SelectTrigger data-testid="filter-status" className="rounded-sm"><SelectValue placeholder="Status" /></SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL}>All statuses</SelectItem>
            {meta.statuses.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={filters.priority || ALL} onValueChange={(v) => update("priority", v === ALL ? "" : v)}>
          <SelectTrigger data-testid="filter-priority" className="rounded-sm"><SelectValue placeholder="Priority" /></SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL}>All priorities</SelectItem>
            {meta.priorities.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={filters.engineer_id || ALL} onValueChange={(v) => update("engineer_id", v === ALL ? "" : v)}>
          <SelectTrigger data-testid="filter-engineer" className="rounded-sm"><SelectValue placeholder="Engineer" /></SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL}>All engineers</SelectItem>
            {engineers.map((e) => <SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>)}
          </SelectContent>
        </Select>
        <Input type="date" value={filters.date_from} data-testid="filter-date-from"
          onChange={(e) => update("date_from", e.target.value)} className="rounded-sm" />
        <Input type="date" value={filters.date_to} data-testid="filter-date-to"
          onChange={(e) => update("date_to", e.target.value)} className="rounded-sm" />
        <div className="sm:col-span-2 lg:col-span-4 flex gap-2">
          <Button onClick={load} data-testid="apply-filters" className="rounded-sm bg-ink hover:bg-ink/90 text-white">
            <Search size={15} className="mr-1" /> Search
          </Button>
          <Button variant="outline" data-testid="clear-filters" className="rounded-sm" onClick={() => {
            setFilters({ complaint_no: "", mobile: "", bill_number: "", status: "", priority: "", engineer_id: "", date_from: "", date_to: "", flag: "" });
            setParams({});
            setTimeout(load, 0);
          }}>Clear</Button>
        </div>
      </div>

      {/* Table */}
      <div className="border border-border bg-white overflow-x-auto">
        <table className="w-full text-sm" data-testid="complaints-table">
          <thead>
            <tr className="border-b border-border bg-secondary text-left label-caps text-muted-foreground">
              <th className="px-4 py-3">Complaint #</th>
              <th className="px-4 py-3">Customer</th>
              <th className="px-4 py-3 hidden md:table-cell">Appliance</th>
              <th className="px-4 py-3">Priority</th>
              <th className="px-4 py-3">Status</th>
              <th className="px-4 py-3 hidden lg:table-cell">Engineer</th>
              <th className="px-4 py-3">Flags</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan="7" className="px-4 py-10 text-center text-muted-foreground">Loading…</td></tr>
            ) : list.length === 0 ? (
              <tr><td colSpan="7" className="px-4 py-10 text-center text-muted-foreground">No complaints match.</td></tr>
            ) : list.map((c) => (
              <tr key={c.id} data-testid={`complaint-row-${c.complaint_no}`}
                onClick={() => navigate(`/complaints/${c.id}`)}
                className="border-b border-border hover:bg-secondary cursor-pointer transition-colors">
                <td className="px-4 py-3 font-mono text-xs font-semibold text-klein">{c.complaint_no}</td>
                <td className="px-4 py-3">{c.customer_name}<div className="text-xs text-muted-foreground">{c.customer_mobile}</div></td>
                <td className="px-4 py-3 hidden md:table-cell">{c.appliance_type}</td>
                <td className="px-4 py-3"><PriorityBadge priority={c.priority} /></td>
                <td className="px-4 py-3"><StatusBadge status={c.status} /></td>
                <td className="px-4 py-3 hidden lg:table-cell">{c.engineer?.name || <span className="text-muted-foreground">—</span>}</td>
                <td className="px-4 py-3">
                  <div className="flex gap-1 flex-wrap">
                    {c.flags?.assign_overdue && <span className="text-[10px] font-bold bg-warn px-1.5 py-0.5">24H</span>}
                    {c.flags?.attend_breach && <span className="text-[10px] font-bold bg-signal text-white px-1.5 py-0.5">72H</span>}
                    {c.flags?.recurring && <Flag size={13} className="text-signal" />}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
